import { useState } from 'react';
import { styled } from 'styled-components';

import { Accordion } from '#/components/Accordion/Accordion';
import { AccordionItem } from '#/components/Accordion/Item/AccordionItem';
import { Icon } from '#/components/Icon/Icon';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import { colors, spacing } from '#/theme/theme';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { ViewOrgPageTruncatedListings } from './components/ViewOrgPageTruncatedListings';
import type { ViewOrgPageListingTab } from './types/ViewOrgPageListingTab';
import { ViewOrgPageListingsTabsTabLabel } from './ViewOrgPageListingsTabsLabel';

type Props = {
  org: ApiOrg;
  defaultTab: ViewOrgPageListingTab;
};

const StyledSummary = styled.summary`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${spacing[12]};
  padding-bottom: ${spacing[12]};
`;

const StyledAccordionItem = styled(AccordionItem)`
  border-bottom: 1px solid ${colors.calloutGrey};
`;

export function ViewOrgPageListingsTabsMobile({ org, defaultTab }: Props) {
  const { i18n } = useI18n();

  const [currentTab, setCurrentTab] = useState<ViewOrgPageListingTab | null>(
    defaultTab,
  );

  return (
    <Accordion>
      {org.jobs.length > 0 && (
        <StyledAccordionItem
          defaultOpen
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              trackClicked('Listing Type Tab on Org Page (Mobile)', {
                listing_type: 'JOB',
              });
              if (open) return 'JOB';
              if (prevTab === 'JOB') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'JOB'}
                count={org.jobCount}
                icon={<Icon name="briefcase" colorName="white" size={24} />}
              >
                {i18n('Jobs')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'JOB' ? 'up' : 'down'}`}
                colorName="darkContentGrey"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings
            listingCount={org.jobCount}
            listingType="JOB"
            listings={org.jobs}
            orgId={org.id}
          />
        </StyledAccordionItem>
      )}

      {org.internships.length > 0 && (
        <StyledAccordionItem
          defaultOpen={defaultTab === 'INTERNSHIP'}
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              trackClicked('Listing Type Tab on Org Page (Mobile)', {
                listing_type: 'INTERNSHIP',
              });
              if (open) return 'INTERNSHIP';
              if (prevTab === 'INTERNSHIP') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'INTERNSHIP'}
                count={org.internshipCount}
                icon={<Icon name="sign-path" colorName="white" size={24} />}
              >
                {i18n('Internships')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'INTERNSHIP' ? 'up' : 'down'}`}
                colorName="black"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings
            listingCount={org.internshipCount}
            listingType="INTERNSHIP"
            listings={org.internships}
            orgId={org.id}
          />
        </StyledAccordionItem>
      )}

      {org.volops.length > 0 && (
        <StyledAccordionItem
          defaultOpen={defaultTab === 'VOLOP'}
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              trackClicked('Listing Type Tab on Org Page (Mobile)', {
                listing_type: 'VOLOP',
              });
              if (open) return 'VOLOP';
              if (prevTab === 'VOLOP') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'VOLOP'}
                count={org.volopCount}
                icon={<Icon name="hand" colorName="white" size={24} />}
              >
                {i18n('Volunteer opportunities')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'VOLOP' ? 'up' : 'down'}`}
                colorName="darkContentGrey"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings
            listingCount={org.volopCount}
            listingType="VOLOP"
            listings={org.volops}
            orgId={org.id}
          />
        </StyledAccordionItem>
      )}

      {org.events.length > 0 && (
        <StyledAccordionItem
          defaultOpen={defaultTab === 'EVENT'}
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              trackClicked('Listing Type Tab on Org Page (Mobile)', {
                listing_type: 'EVENT',
              });
              if (open) return 'EVENT';
              if (prevTab === 'EVENT') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'EVENT'}
                count={org.eventCount}
                icon={<Icon name="star-calendar" colorName="white" size={24} />}
              >
                {i18n('Events')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'EVENT' ? 'up' : 'down'}`}
                colorName="darkContentGrey"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings
            listingCount={org.eventCount}
            listingType="EVENT"
            listings={org.events}
            orgId={org.id}
          />
        </StyledAccordionItem>
      )}
    </Accordion>
  );
}
